import { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';

interface UserPurchases {
  documents: Set<string>;
  courses: Set<string>;
  loading: boolean;
}

export function useUserPurchases() {
  const { user } = useAuth();
  const [purchases, setPurchases] = useState<UserPurchases>({
    documents: new Set(),
    courses: new Set(),
    loading: true,
  });

  useEffect(() => {
    if (user) {
      fetchUserPurchases();
    } else {
      setPurchases({
        documents: new Set(),
        courses: new Set(),
        loading: false,
      });
    }
  }, [user]);

  const fetchUserPurchases = async () => {
    if (!user) return;

    try {
      setPurchases(prev => ({ ...prev, loading: true }));

      // Fetch completed document purchases
      const { data: docPurchases } = await supabase
        .from('document_purchases')
        .select('document_id')
        .eq('user_id', user.id)
        .eq('payment_status', 'completed');

      // Fetch completed course purchases
      const { data: coursePurchases } = await supabase
        .from('course_purchases')
        .select('course_id')
        .eq('user_id', user.id)
        .eq('payment_status', 'completed');

      const documentIds = new Set(docPurchases?.map(p => p.document_id) || []);
      const courseIds = new Set(coursePurchases?.map(p => p.course_id) || []);

      setPurchases({
        documents: documentIds,
        courses: courseIds,
        loading: false,
      });
    } catch (error) {
      console.error('Error fetching user purchases:', error);
      setPurchases({
        documents: new Set(),
        courses: new Set(),
        loading: false,
      });
    }
  };

  const hasDocument = (documentId: string) => purchases.documents.has(documentId);
  const hasCourse = (courseId: string) => purchases.courses.has(courseId);
  const hasItem = (itemId: string, itemType: 'document' | 'course') => {
    return itemType === 'document' ? hasDocument(itemId) : hasCourse(itemId);
  };

  // Refresh purchases after a successful purchase
  const refreshPurchases = () => {
    if (user) {
      fetchUserPurchases();
    }
  };

  return {
    ...purchases,
    hasDocument,
    hasCourse,
    hasItem,
    refreshPurchases,
  };
}